import {
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  Link,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Text,
} from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import { channelValueOf, getDeliveryChannel } from '@/features/deliveries';
import type { Action } from '@/services/db/action/entity';
import { useActionOptional } from '@/services/db/action/queries';
import type { Campaign } from '@/services/db/campaign/entity';
import { useCampaignOptional } from '@/services/db/campaign/queries';
import { customerName } from '@/services/db/customer/display';
import { getCustomerQueryOptions } from '@/services/db/customer/queries';
import type { Delivery } from '@/services/db/delivery/entity';
import { metaValue } from '@/services/db/delivery/utils';
import type { Newsletter } from '@/services/db/newsletter/entity';
import { useNewsletterOptional } from '@/services/db/newsletter/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import type { TransactionalMessage } from '@/services/db/transactional-message/entity';
import { useTransactionalMessageOptional } from '@/services/db/transactional-message/queries';
import { generatePath } from '@/services/router/generate-path';
import { capitalize, isEmptyString, titleize } from '@/utils/string';

type DeliveryDetailMetadataProps = { delivery: Delivery };

/**
 * The delivery's summary metadata (`Metadata` panel). The id comes straight off
 * the list-row `Delivery`; the campaign / broadcast / action / newsletter /
 * transactional links resolve related models via FK-gated optional hooks.
 * Matches Ember's Metadata panel, which carries neither a recipient nor a status
 * row: the recipient is the message preview's `To` line and the status is the
 * preview header's state tag. The Trigger row is deferred (FPS-3194): it needs a
 * broadcast-trigger data-layer service.
 */
export function DeliveryDetailMetadata({ delivery }: DeliveryDetailMetadataProps) {
  const workspaceId = requireWorkspaceId();
  const originalDeliveryId = metaValue(delivery, 'orig_delivery_id');
  const conversationId = delivery.conversation_id;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Metadata</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <DescriptionList>
          <DescriptionListTerm>id</DescriptionListTerm>
          <DescriptionListDetails>{delivery.id}</DescriptionListDetails>

          <PersonRow delivery={delivery} workspaceId={workspaceId} />

          <RelatedMessageRows delivery={delivery} workspaceId={workspaceId} />

          {!isEmptyString(originalDeliveryId) ? (
            <>
              <DescriptionListTerm>Orig. delivery</DescriptionListTerm>
              <DescriptionListDetails>
                <MetadataLink
                  href={generatePath('/workspaces/:workspaceId/journeys/outbox/deliveries/:deliveryId', {
                    workspaceId,
                    deliveryId: originalDeliveryId,
                  })}
                >
                  {originalDeliveryId}
                </MetadataLink>
              </DescriptionListDetails>
            </>
          ) : null}

          {!isEmptyString(conversationId) ? (
            <>
              <DescriptionListTerm>Conversation ID</DescriptionListTerm>
              <DescriptionListDetails className="fs-mask">{conversationId}</DescriptionListDetails>
            </>
          ) : null}
        </DescriptionList>
      </PanelSection>
    </Panel>
  );
}

type PersonRowProps = { delivery: Delivery; workspaceId: number };

/**
 * The Person row (Ember `CustomerLabel`): a deleted customer reads "deleted", an
 * identified one links to their profile, and an unidentified delivery reads
 * "Anonymous". The row is hidden for data (webhook) campaigns, whose deliveries
 * aren't tied to a person (Ember `not campaign.isDataCampaign`). The label itself
 * is derived in `ProfileLabel`.
 */
function PersonRow({ delivery, workspaceId }: PersonRowProps) {
  const campaignId = delivery.campaign_id != null ? String(delivery.campaign_id) : '';
  const { data: campaign } = useCampaignOptional({ workspaceId, id: campaignId });

  // A campaign-tied delivery only shows the Person row once its campaign resolves
  // to a non-data type — data (webhook) campaigns aren't tied to a person (Ember
  // `not campaign.isDataCampaign`). While the campaign is still loading or failed
  // to load (`retry: false`), render nothing rather than flashing the row in and
  // then out, or stranding it visible on a failed read — the way RelatedMessageRows
  // gates on a resolved model.
  if (campaignId !== '' && campaign == null) return null;
  if (campaign?.type === 'webhook') return null;

  return (
    <>
      <DescriptionListTerm>Person</DescriptionListTerm>
      <DescriptionListDetails className="fs-mask">
        <PersonLabel delivery={delivery} workspaceId={workspaceId} />
      </DescriptionListDetails>
    </>
  );
}

function PersonLabel({ delivery, workspaceId }: PersonRowProps) {
  if (delivery.customer_deleted === true) {
    return <Text color="subtle">deleted</Text>;
  }
  const customerId = delivery.customer_id;
  if (isEmptyString(customerId)) {
    return <>Anonymous</>;
  }
  const href = generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
    workspaceId,
    personId: customerId,
  });
  return <ProfileLabel customerId={customerId} workspaceId={workspaceId} href={href} />;
}

type ProfileLabelProps = { customerId: string; workspaceId: number; href: string };

/**
 * Ember's `CustomerLabel` derivation: the **fetched profile's** name, else the id.
 * Deliberately ignores the delivery's denormalized `customer_name` — that field often
 * holds the recipient email, which Ember never shows here. While the read is pending, or
 * if it fails (`retry: false`, e.g. a deleted profile), the id stands in — Ember's final
 * fallback. Ember has a middle step for the account's configured identifier attribute;
 * that config isn't in the React data layer yet, so a profile with no name falls straight
 * through to the id (which is what Ember renders for such profiles anyway).
 */
function ProfileLabel({ customerId, workspaceId, href }: ProfileLabelProps) {
  const { data: customer } = useQuery({ ...getCustomerQueryOptions(workspaceId, customerId), retry: false });
  const name = customer != null ? customerName(customer.attributes ?? {}) : null;
  return <MetadataLink href={href}>{name ?? customerId}</MetadataLink>;
}

type MetadataLinkProps = { href: string; children: string };

/** The shared related-model link style: secondary, not underlined, trailing chevron. */
function MetadataLink({ href, children }: MetadataLinkProps) {
  return (
    <Link href={href} variant="secondary" isUnderlined={false} icon="chevron-right" iconPosition="trailing">
      {children}
    </Link>
  );
}

type RelatedMessageRowsProps = { delivery: Delivery; workspaceId: number };

/**
 * The campaign / broadcast, newsletter, and transactional-message rows are
 * mutually exclusive — a delivery carries exactly one of the three FKs. The
 * campaign branch also nests the action row.
 */
function RelatedMessageRows({ delivery, workspaceId }: RelatedMessageRowsProps) {
  const campaignId = delivery.campaign_id != null ? String(delivery.campaign_id) : '';
  const newsletterId = delivery.newsletter_id != null ? String(delivery.newsletter_id) : '';
  const transactionalMessageId =
    delivery.transactional_message_id != null ? String(delivery.transactional_message_id) : '';
  const actionId = delivery.action_id != null ? String(delivery.action_id) : '';

  const { data: campaign } = useCampaignOptional({ workspaceId, id: campaignId });
  const { data: newsletter } = useNewsletterOptional({ workspaceId, id: newsletterId });
  const { data: transactionalMessage } = useTransactionalMessageOptional({ workspaceId, id: transactionalMessageId });
  const { data: action } = useActionOptional({ workspaceId, id: actionId, enabled: campaignId !== '' });

  if (campaign != null) {
    return (
      <>
        <CampaignRow campaign={campaign} workspaceId={workspaceId} />
        {/* Trigger row deferred (FPS-3194): the broadcast-trigger model has no React data-layer service yet. */}
        {action != null ? <ActionRow delivery={delivery} action={action} workspaceId={workspaceId} /> : null}
      </>
    );
  }
  if (newsletter != null) {
    return <NewsletterRow newsletter={newsletter} workspaceId={workspaceId} />;
  }
  if (transactionalMessage != null) {
    return <TransactionalMessageRow message={transactionalMessage} workspaceId={workspaceId} />;
  }
  return null;
}

type CampaignRowProps = { campaign: Campaign; workspaceId: number };

function CampaignRow({ campaign, workspaceId }: CampaignRowProps) {
  const isApiTriggered = campaign.type === 'triggered_broadcast';
  const term = isApiTriggered ? 'Broadcast' : 'Automation';
  const href = isApiTriggered
    ? generatePath('/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview', {
        workspaceId,
        broadcastId: campaign.id,
      })
    : generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview', {
        workspaceId,
        campaignId: campaign.id,
      });
  const name = isEmptyString(campaign.name) ? '(No name)' : campaign.name;

  return (
    <>
      <DescriptionListTerm>{term}</DescriptionListTerm>
      <DescriptionListDetails>
        <MetadataLink href={href}>{name}</MetadataLink>
      </DescriptionListDetails>
    </>
  );
}

type ActionRowProps = { delivery: Delivery; action: Action; workspaceId: number };

function ActionRow({ delivery, action, workspaceId }: ActionRowProps) {
  // FLO-3165: the delivery's historical action type differs from the action's
  // current type (e.g. an email converted to an A/B test), so the composer link
  // would be broken — show "Action no longer exists" instead.
  const actionTypeChanged = delivery.action_type !== action.type;
  const name = isEmptyString(action.name) ? '(No name)' : action.name;
  const href = generatePath('/workspaces/:workspaceId/journeys/composer/actions/:actionId', {
    workspaceId,
    actionId: action.id,
  });

  return (
    <>
      <DescriptionListTerm>{actionTermLabel(delivery, action)}</DescriptionListTerm>
      <DescriptionListDetails>
        {actionTypeChanged ? (
          <Text color="subtle">Action no longer exists</Text>
        ) : (
          <MetadataLink href={href}>{name}</MetadataLink>
        )}
      </DescriptionListDetails>
    </>
  );
}

type NewsletterRowProps = { newsletter: Newsletter; workspaceId: number };

function NewsletterRow({ newsletter, workspaceId }: NewsletterRowProps) {
  const href = generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId', {
    workspaceId,
    newsletterId: newsletter.id,
  });
  const name = isEmptyString(newsletter.name) ? '(No name)' : newsletter.name;

  return (
    <>
      <DescriptionListTerm>One-time send</DescriptionListTerm>
      <DescriptionListDetails>
        <MetadataLink href={href}>{name}</MetadataLink>
      </DescriptionListDetails>
    </>
  );
}

type TransactionalMessageRowProps = { message: TransactionalMessage; workspaceId: number };

function TransactionalMessageRow({ message, workspaceId }: TransactionalMessageRowProps) {
  const href = generatePath('/workspaces/:workspaceId/journeys/transactional/:transactionalMessageId', {
    workspaceId,
    transactionalMessageId: message.id,
  });
  const name = isEmptyString(message.name) ? '(No name)' : message.name;

  return (
    <>
      <DescriptionListTerm>Message</DescriptionListTerm>
      <DescriptionListDetails>
        <MetadataLink href={href}>{name}</MetadataLink>
      </DescriptionListDetails>
    </>
  );
}

/**
 * The action row's term label: the titleized channel short name for push /
 * Airship actions, otherwise the delivery's historical action-type name
 * (capitalized). Delivery `action_type`s are all messaging channels, so the
 * channel value equals the historical type name here.
 */
function actionTermLabel(delivery: Delivery, action: Action): string {
  if (action.type === 'push_action' || action.type === 'urban_airship_action') {
    return titleize(getDeliveryChannel(channelValueOf(action.type))?.shortName ?? '');
  }
  return capitalize(channelValueOf(delivery.action_type));
}
